import {TargetBlank} from '@wandb/weave/common/util/links';
import React, {FC, memo, useCallback, useState} from 'react';
import {shallowEqual} from 'react-redux';
import {Dropdown} from 'semantic-ui-react';

import {ReactComponent as IconHome} from '../../assets/SVG/icon-home.svg';
import {envIsPublicCloud} from '../../config';
import {useSelector} from '../../state/hooks';
import * as WorkspaceActions from '../../state/workspaces/actions';
import {useWorkspaceAction} from '../../state/workspaces/hooks';
import {useAdminModeActive} from '../../util/admin';
import DOC_URLS from '../../util/doc_urls';
import * as S from './ViewBar.styles';

interface ViewMenuProps {
  workspaceID: string;
  trigger: React.ReactNode;
  currentView: string;
  isOwnView?: boolean | null;
}

type AllViewMenuProps = ViewMenuProps & ReturnType<typeof useViewMenuProps>;

const ViewMenuComp: FC<AllViewMenuProps> = ({
  trigger,
  currentView,
  isOwnView,
  views,
  loadDefault,
  load,
  clearView,
  copyToDefault,
}) => {
  const viewerIsAdmin = useAdminModeActive();
  // The ViewMenu shows a profile picture for each user who has a workspace.
  // Maintain the open state ourselves so we can only render the list of
  // workspaces when the menu is open, to avoid loading the profile pictures
  // until they are needed.
  const [open, setOpen] = useState(false);
  const onOpen = useCallback(() => setOpen(true), []);
  const onClose = useCallback(() => setOpen(false), []);
  return (
    <Dropdown
      floating
      className="view-menu dropdown-menu-text-icon-button"
      onOpen={onOpen}
      onClose={onClose}
      open={open}
      trigger={trigger}
      direction="right">
      <Dropdown.Menu>
        <Dropdown.Header
          content="Load another workspace"
          className="small-header"
        />
        <div className="scroll-container">
          {open && (
            <>
              {currentView !== 'default' && (
                <Dropdown.Item
                  onClick={() => {
                    loadDefault();
                    window.analytics?.track('Load another workspace clicked', {
                      isDefault: true,
                      isCurrentUsersWorkspace: isOwnView,
                    });
                  }}
                  className="load-default-workspace-item"
                  icon={
                    <S.DefaultHomeIcon>
                      <IconHome />
                    </S.DefaultHomeIcon>
                  }
                  text="Default"
                />
              )}
              {views
                .filter(view => view.user.username !== currentView)
                .filter(
                  view =>
                    // In the public cloud environment, only W&B super-admins
                    // can see other W&B super-admins' workspaces.
                    // This is to prevent users from seeing workspaces which are
                    // created when we debug their projects.
                    !envIsPublicCloud || !view.user.admin || viewerIsAdmin
                )
                .map(view => (
                  <Dropdown.Item
                    key={view.cid}
                    onClick={() => {
                      load(view.cid);
                      window.analytics?.track(
                        'Load another workspace clicked',
                        {
                          isDefault: false,
                          isCurrentUsersWorkspace: isOwnView,
                        }
                      );
                    }}
                    image={{
                      avatar: true,
                      src: view.user.photoUrl,
                      onError: (img: any) =>
                        (img.target.style.visibility = 'hidden'),
                    }}
                    content={view.user.username}
                  />
                ))}
            </>
          )}
        </div>
        {isOwnView && (
          <>
            <Dropdown.Divider />
            <Dropdown.Header
              className="small-header"
              content="Workspace actions"
            />
            <Dropdown.Item
              text="Clear workspace"
              onClick={() => {
                clearView();
                window.analytics?.track('Clear workspace clicked');
              }}
            />
            <Dropdown.Item
              text="Copy to default workspace"
              onClick={() => {
                copyToDefault();
                window.analytics?.track('Copy to default workspace clicked');
              }}
            />
          </>
        )}
        <>
          <Dropdown.Divider />
          <Dropdown.Header
            className="small-header"
            content="Workspace documentation"
          />
          <Dropdown.Item
            as={TargetBlank}
            href={DOC_URLS.workspaces}
            onClick={() =>
              window.analytics?.track('How to use workspaces clicked')
            }
            text="How to use workspaces"
          />
        </>
      </Dropdown.Menu>
    </Dropdown>
  );
};

const ViewMenuCompMemo = memo(ViewMenuComp);

function useViewMenuProps(props: ViewMenuProps) {
  const workspaceViews = useSelector(state => {
    const workspace = state.workspaces[props.workspaceID];
    if (workspace.viewListID == null) {
      return [];
    }
    const metadataQuery = state.views.lists[workspace.viewListID];
    if (metadataQuery == null) {
      throw new Error('Unexpected state');
    }
    return metadataQuery.viewIds
      .map(vid => state.views.views[vid])
      .filter(v => v.name === 'workspace');
  }, shallowEqual);
  const copyToDefault = useWorkspaceAction(
    props.workspaceID,
    WorkspaceActions.copyToDefault
  );
  const clearView = useWorkspaceAction(
    props.workspaceID,
    WorkspaceActions.clearView
  );
  const load = useWorkspaceAction(props.workspaceID, WorkspaceActions.load);
  const loadDefault = useWorkspaceAction(
    props.workspaceID,
    WorkspaceActions.loadDefault
  );
  return {
    views: workspaceViews,
    copyToDefault,
    clearView,
    load,
    loadDefault,
  };
}

export const ViewMenu = (props: ViewMenuProps) => {
  const selectedProps = useViewMenuProps(props);
  return <ViewMenuCompMemo {...props} {...selectedProps} />;
};
